import React, { FC, useMemo, useRef, useState } from 'react';

import {
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { UploadFile } from '@mui/icons-material';

import { formMsg, notifyMsg } from '../../configs';
import { AlertBar } from '../../components/AlertBar';
import { useSnack } from '../../util/useSnack';
import { parseCsv } from '../../util/csv';
import { useAddKeyValues, KeyValue } from '../../api-http/kvs';

const KEY_PATTERN = /^[0-9A-Z_]+$/;

interface ImportRow {
  key: string;
  value: string;
  error?: string;
}

interface ImportKvsCsvFormProps {
  kvsGroup: string | undefined;
  existingKeys: string[];
  onFinished: () => void;
  onRefetchData: () => void;
}

const buildRows = (text: string, existingKeys: Set<string>): ImportRow[] => {
  const parsed = parseCsv(text);
  // The exported CSV (and the expected import format) starts with a
  // "Key,Value" header row, so skip the first line.
  const dataRows = parsed.slice(1);
  const seenKeys = new Set<string>();

  return dataRows.map(([rawKey, rawValue]) => {
    const key = (rawKey || '').trim().toUpperCase();
    const value = (rawValue || '').trim();

    let error: string | undefined;
    if (!key) {
      error = 'Key is required';
    } else if (!KEY_PATTERN.test(key)) {
      error = 'Key must contain only uppercase letters, numbers, or _';
    } else if (seenKeys.has(key)) {
      error = 'Duplicate key in this file';
    } else if (existingKeys.has(key)) {
      error = 'Key already exists in this group';
    } else if (!value) {
      error = 'Value is required';
    }

    if (!error) {
      seenKeys.add(key);
    }

    return { key, value, error };
  });
};

export const ImportKvsCsvForm: FC<ImportKvsCsvFormProps> = ({
  kvsGroup,
  existingKeys,
  onFinished,
  onRefetchData,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showErrSnack, showSuccessSnack } = useSnack();
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [parseError, setParseError] = useState('');

  const existingKeySet = useMemo(() => new Set(existingKeys), [existingKeys]);

  const { isLoading, mutate, error } = useAddKeyValues({
    onSuccess: () => {
      showSuccessSnack(notifyMsg.KVS_KEY_VALUE_IMPORT_SUCCESS);
      onRefetchData();
      onFinished();
    },
    onError: (err) => {
      showErrSnack(err || notifyMsg.KVS_KEY_VALUE_IMPORT_FAILED);
    },
  });

  const validRows = rows.filter((row) => !row.error);
  const invalidRows = rows.filter((row) => row.error);

  const onChooseFile = () => fileInputRef.current?.click();

  const onFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setFileName(file.name);
    setParseError('');

    const text = await file.text();
    const parsedRows = buildRows(text, existingKeySet);

    if (!parsedRows.length) {
      setRows([]);
      setParseError(formMsg.CSV_NO_VALID_ROWS);
      return;
    }

    setRows(parsedRows);
  };

  const onImport = () => {
    if (!validRows.length) return;

    const kvsData: KeyValue[] = validRows.map(({ key, value }) => ({
      key,
      value,
    }));

    mutate({ kvsGroup: kvsGroup || '', kvsData });
  };

  return (
    <Stack spacing={2} sx={{ pt: 1, pb: 1 }}>
      <Typography sx={{ color: '#64748b', fontSize: 14 }}>
        Import a CSV file exported from the KVS group on another system. The
        first row is treated as a header (Key, Value) and skipped.
      </Typography>

      <AlertBar
        severity="error"
        msg={error?.msg || parseError}
        sx={{ mb: 0 }}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        hidden
        onChange={onFileSelected}
      />

      <Stack direction="row" spacing={1.5} alignItems="center">
        <Button
          variant="outlined"
          startIcon={<UploadFile />}
          onClick={onChooseFile}
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          Choose CSV file
        </Button>
        {fileName && (
          <Typography sx={{ color: '#334155', fontSize: 14 }}>
            {fileName}
          </Typography>
        )}
      </Stack>

      {rows.length > 0 && (
        <>
          <Stack direction="row" spacing={1}>
            <Chip
              size="small"
              color="success"
              label={`${validRows.length} ready to import`}
            />
            {invalidRows.length > 0 && (
              <Chip
                size="small"
                color="warning"
                label={`${invalidRows.length} will be skipped`}
              />
            )}
          </Stack>

          <TableContainer
            sx={{
              maxHeight: 320,
              border: '1px solid #e2e8f0',
              borderRadius: 2,
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={`${row.key}-${index}`}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {row.key || '-'}
                    </TableCell>
                    <TableCell>{row.value || '-'}</TableCell>
                    <TableCell>
                      {row.error ? (
                        <Typography sx={{ color: '#dc2626', fontSize: 13 }}>
                          {row.error}
                        </Typography>
                      ) : (
                        <Typography sx={{ color: '#16a34a', fontSize: 13 }}>
                          OK
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Box>
        <Button
          color="primary"
          variant="contained"
          fullWidth
          disabled={!validRows.length || isLoading}
          onClick={onImport}
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          {validRows.length
            ? `Import ${validRows.length} key value${
                validRows.length === 1 ? '' : 's'
              }`
            : 'Import'}
        </Button>
      </Box>
    </Stack>
  );
};
